import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { MessageSquare, Plus, Send, Users, User, Loader2, Search, ArrowUpRight, CheckCircle2, Check } from "lucide-react";
import { toast } from "sonner";

function timeLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString())
    return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function Messages() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const bottomRef = useRef(null);

  const loadConversations = useCallback(async () => {
    try { const { data } = await api.get("/conversations"); setConversations(data); } catch (err) { console.debug("Load conversations failed (ignored):", err); }
  }, []);

  const loadMessages = useCallback(async (id) => {
    if (!id) return;
    try { const { data } = await api.get(`/conversations/${id}/messages`); setActive(data); } catch (err) { console.debug("Load messages failed (ignored):", err); }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  // Open a specific conversation passed via ?c=<id> (e.g. from a client's "Discuss" button).
  useEffect(() => {
    const c = searchParams.get("c");
    if (c) { setActiveId(c); setSearchParams({}, { replace: true }); }
  }, [searchParams, setSearchParams]);

  useEffect(() => { if (activeId) loadMessages(activeId); }, [activeId, loadMessages]);

  // Light polling for new messages + conversation list.
  useEffect(() => {
    const t = setInterval(() => {
      loadConversations();
      if (activeId) loadMessages(activeId);
    }, 5000);
    return () => clearInterval(t);
  }, [activeId, loadConversations, loadMessages]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [active?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !activeId) return;
    setSending(true);
    const content = text.trim();
    setText("");
    try {
      await api.post(`/conversations/${activeId}/messages`, { content });
      await loadMessages(activeId);
      loadConversations();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send");
      setText(content);
    } finally { setSending(false); }
  };

  const onCreated = (conv) => {
    setNewOpen(false);
    loadConversations();
    setActiveId(conv.id);
  };

  const [acking, setAcking] = useState(false);
  const acknowledge = async () => {
    if (!activeId) return;
    setAcking(true);
    try {
      await api.post(`/conversations/${activeId}/acknowledge`);
      toast.success("Assignment acknowledged");
      await loadMessages(activeId);
      loadConversations();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not acknowledge");
    } finally { setAcking(false); }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="messages-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter text-gray-900">Messages</h1>
          <p className="text-gray-500 text-sm mt-1">Chat with your team — direct messages and group channels.</p>
        </div>
        <button onClick={() => setNewOpen(true)} data-testid="new-conversation-button"
          className="flex items-center gap-1.5 bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm">
          <Plus className="w-4 h-4" /> New message
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 bg-white border border-gray-200 rounded-sm shadow-sm overflow-hidden" style={{ height: "calc(100vh - 220px)" }}>
        {/* Conversation list */}
        <div className="border-r border-gray-100 overflow-y-auto" data-testid="conversation-list">
          {conversations.length === 0 ? (
            <p className="text-sm text-gray-400 p-6 text-center">No conversations yet. Start one with “New message”.</p>
          ) : conversations.map((c) => (
            <button key={c.id} onClick={() => setActiveId(c.id)} data-testid={`conversation-${c.id}`}
              className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors flex items-start gap-3 ${activeId === c.id ? "bg-blue-50" : ""}`}>
              <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${c.type === "group" ? "bg-indigo-100 text-indigo-700" : "bg-[#002FA7]/10 text-[#002FA7]"}`}>
                {c.type === "group" ? <Users className="w-4 h-4" /> : <User className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-gray-900 truncate">{c.name}</span>
                  <span className="text-xs text-gray-400 shrink-0">{timeLabel(c.last_message_at)}</span>
                </div>
                {c.client_id && (
                  <span className="inline-block text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 px-1.5 rounded-sm mb-0.5">CASE</span>
                )}
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-gray-500 truncate">{c.last_message_preview || "No messages yet"}</span>
                  {c.unread > 0 && (
                    <span data-testid={`unread-${c.id}`} className="shrink-0 bg-[#002FA7] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">{c.unread}</span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Active thread */}
        <div className="lg:col-span-2 flex flex-col">
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
              <MessageSquare className="w-10 h-10 mb-3" strokeWidth={1.5} />
              <p className="text-sm">Select a conversation to start chatting</p>
            </div>
          ) : (
            <>
              <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-3">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center ${active.type === "group" ? "bg-indigo-100 text-indigo-700" : "bg-[#002FA7]/10 text-[#002FA7]"}`}>
                  {active.type === "group" ? <Users className="w-4 h-4" /> : <User className="w-4 h-4" />}
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900" data-testid="active-conversation-name">{active.name}</p>
                  {active.client_id ? (
                    <Link to={`/clients/${active.client_id}`} data-testid="active-conversation-client-link"
                      className="text-xs text-[#002FA7] hover:underline inline-flex items-center gap-0.5">
                      Linked to {active.client_name} <ArrowUpRight className="w-3 h-3" />
                    </Link>
                  ) : active.type === "group" && (
                    <p className="text-xs text-gray-400">{active.members.map((m) => m.name).join(", ")}</p>
                  )}
                </div>
              </div>
              {active.is_case && (
                <div className="px-5 py-2.5 bg-emerald-50/60 border-b border-emerald-100 flex items-center justify-between gap-3 flex-wrap" data-testid="ack-bar">
                  <div className="flex items-center gap-2 text-xs text-emerald-800 min-w-0">
                    <CheckCircle2 className="w-4 h-4 shrink-0" />
                    {active.acks?.length ? (
                      <span data-testid="ack-summary" className="truncate">
                        <strong>Acknowledged by {active.acks.length}:</strong> {active.acks.map((a) => a.name).join(", ")}
                      </span>
                    ) : (
                      <span data-testid="ack-summary">No one has acknowledged this assignment yet.</span>
                    )}
                  </div>
                  {active.my_ack ? (
                    <span data-testid="ack-done" className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 shrink-0">
                      <Check className="w-3.5 h-3.5" /> You acknowledged
                    </span>
                  ) : (
                    <button onClick={acknowledge} disabled={acking} data-testid="acknowledge-button"
                      className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-sm font-semibold text-xs shrink-0 disabled:opacity-60">
                      {acking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Acknowledge assignment
                    </button>
                  )}
                </div>
              )}
              <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="messages-thread">
                {active.messages.map((m) => (
                  <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-lg px-3 py-2 ${m.mine ? "bg-[#002FA7] text-white" : "bg-gray-100 text-gray-800"}`}>
                      {!m.mine && active.type === "group" && <p className="text-[11px] font-bold opacity-70 mb-0.5">{m.sender_name}</p>}
                      <p className="text-sm whitespace-pre-wrap break-words">{m.content}</p>
                      <p className={`text-[10px] mt-1 ${m.mine ? "text-blue-100" : "text-gray-400"}`}>{timeLabel(m.created_at)}</p>
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              <form onSubmit={send} className="p-3 border-t border-gray-100 flex items-center gap-2">
                <input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)}
                  placeholder="Type a message…" autoComplete="off"
                  className="flex-1 border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
                <button type="submit" disabled={sending || !text.trim()} data-testid="send-message-button"
                  className="bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-1.5 disabled:opacity-50">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </form>
            </>
          )}
        </div>
      </div>

      <NewConversationDialog open={newOpen} onOpenChange={setNewOpen} onCreated={onCreated} />
    </div>
  );
}

function NewConversationDialog({ open, onOpenChange, onCreated }) {
  const [mode, setMode] = useState("dm");
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (open) {
      setMode("dm"); setSelected([]); setGroupName(""); setSearch("");
      api.get("/messages/contacts").then((r) => setContacts(r.data)).catch(() => setContacts([]));
    }
  }, [open]);

  const toggle = (id) => {
    if (mode === "dm") setSelected([id]);
    else setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const create = async () => {
    if (selected.length === 0) { toast.error("Pick at least one person"); return; }
    if (mode === "group" && !groupName.trim()) { toast.error("Group name is required"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/conversations", { type: mode, member_ids: selected, name: groupName });
      onCreated(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create");
    } finally { setCreating(false); }
  };

  const filtered = contacts.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-sm" data-testid="new-conversation-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">New message</DialogTitle>
          <DialogDescription className="sr-only">Start a direct message or create a group channel with your teammates.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          {[["dm", "Direct message"], ["group", "Group channel"]].map(([v, l]) => (
            <button key={v} onClick={() => { setMode(v); setSelected([]); }} data-testid={`mode-${v}`}
              className={`flex-1 px-3 py-2 rounded-sm text-sm font-semibold border ${mode === v ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-gray-600 border-gray-300"}`}>{l}</button>
          ))}
        </div>
        {mode === "group" && (
          <input data-testid="group-name-input" value={groupName} onChange={(e) => setGroupName(e.target.value)}
            placeholder="Group name" className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
        )}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search teammates…"
            className="w-full border border-gray-300 rounded-sm pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
        </div>
        <div className="max-h-60 overflow-y-auto space-y-1" data-testid="contacts-list">
          {filtered.map((c) => (
            <button key={c.id} onClick={() => toggle(c.id)} data-testid={`contact-${c.id}`}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-sm text-left hover:bg-gray-50 ${selected.includes(c.id) ? "bg-blue-50" : ""}`}>
              <div className="w-8 h-8 rounded-full bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center font-bold text-xs">{c.name?.[0]?.toUpperCase()}</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{c.name}</p>
                <p className="text-xs text-gray-500 truncate">{c.email}</p>
              </div>
              {selected.includes(c.id) && <span className="w-2.5 h-2.5 rounded-full bg-[#002FA7]" />}
            </button>
          ))}
          {filtered.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No teammates found</p>}
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-sm font-semibold text-sm">Cancel</button>
          <button onClick={create} disabled={creating} data-testid="create-conversation-button"
            className="bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
            {creating && <Loader2 className="w-4 h-4 animate-spin" />} Start
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
